import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, AuthScreen, createAuthClient, loadAuthConfig } from '@nsi-advisory-services/auth-react';
import API_CONFIG from './config/apiConfig';
import ThemeProvider from './theme/ThemeProvider';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'leaflet/dist/leaflet.css';
import 'rsuite/dist/rsuite.min.css';
import '@nsi-advisory-services/components/dist/styles.css';
import './index.css';
import './theme/theme-dark.css';

// Dev-only: suppress noisy ResizeObserver loop errors that can spam the overlay.
// These are usually benign and triggered by rapid layout changes.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const resizeObserverErrorRe = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

    window.addEventListener('error', (event) => {
        const msg = String(event?.message || '');
        if (resizeObserverErrorRe.test(msg)) {
            event.preventDefault?.();
            event.stopImmediatePropagation?.();
        }
    });

    const originalConsoleError = console.error;
    console.error = (...args) => {
        const first = args?.[0];
        if (typeof first === 'string' && resizeObserverErrorRe.test(first)) return;
        return originalConsoleError(...args);
    };
}

/**
 * Create QueryClient for React Query - best practice is to create it outside component tree
 */
const queryClient = new QueryClient();

const root = createRoot(document.getElementById('root'));

const start = async () => {
    let config;
    let client;
    try {
        config = await loadAuthConfig(`${API_CONFIG.BASE_URL.replace(/\/+$/, '')}/auth/config`);
        client = await createAuthClient(config, {
            redirectUri: process.env.REACT_APP_REDIRECT_URI || API_CONFIG.FRONTEND_URL,
        });
    } catch (error) {
        console.error('Sign-in setup failed:', error);
        root.render(<AuthScreen title="Can't reach ProPlan Connect" message="Refresh to try again." />);
        return;
    }

    root.render(
        <QueryClientProvider client={queryClient}>
            <AuthProvider client={client} config={config}>
                <ThemeProvider>
                    <App />
                </ThemeProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
};

start();
